// Alphabetical ordering for the name/entity lists that feed dropdowns.
//
// These arrive from Supabase in insert order, which is effectively random to a
// reader hunting for a person or a project in a long <Select>. Sorted once
// where the data lands, so every dropdown fed by that array is ordered without
// each call site having to remember.
//
// Deliberately NOT applied to status, priority or role lists: those are ordered
// by meaning — a workflow pipeline, a severity ramp, a privilege ladder — and
// alphabetising them would read as Closed, Cutover, Hold, Live, Open, Test.

// localeCompare with base sensitivity, so "amit" and "Ashok" sort the way a
// reader expects rather than by code point (uppercase first).
const cmp = (a, b) => String(a || "").localeCompare(String(b || ""), undefined, { sensitivity: "base" });

/** People: falls back to email for an account with no name set yet. */
export const sortByName = (rows = []) => [...rows].sort((a, b) => cmp(a?.name || a?.email, b?.name || b?.email));

/** Projects, initiatives — anything keyed by `title`. */
export const sortByTitle = (rows = []) => [...rows].sort((a, b) => cmp(a?.title, b?.title));

/** Plain strings, e.g. the distinct entity names on the Projects filter. */
export const sortStrings = (values = []) => [...values].sort(cmp);

/**
 * Keys sorted by the LABEL the reader actually sees, which is rarely the same
 * order as the keys: `wip` renders as "WIP", `on_hold` as "On Hold". Used for
 * the status / priority / role dropdowns.
 *
 * Note these lists are ordered by MEANING in their source constants — a
 * workflow pipeline, a severity ramp, a privilege ladder — and that order is
 * still what drives things like the Tasks metrics strip and the cascading
 * filters. This only reorders how the options are listed in a dropdown.
 */
export const sortByLabel = (keys = [], labelOf = (k) => k) =>
  [...keys].sort((a, b) => cmp(labelOf(a), labelOf(b)));
